import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { User, Edit2, Loader2, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  getVolunteerProfileByUserId,
  createVolunteerProfile,
  updateVolunteerProfile,
} from '@/lib/firebase/queries/volunteerProfiles';
import type { VolunteerProfile } from '@/types/volunteer';
import { toast } from 'sonner';

export function VolunteerProfileCard() {
  const { t, dir } = useLanguage();
  const { userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<VolunteerProfile | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Form fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [scfhsNumber, setScfhsNumber] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [volunteerAreas, setVolunteerAreas] = useState('');

  useEffect(() => {
    if (userData?.id) {
      loadProfile();
    }
  }, [userData?.id]);

  const loadProfile = async () => {
    if (!userData?.id) return;
    
    setLoading(true);
    try {
      const existingProfile = await getVolunteerProfileByUserId(userData.id);
      setProfile(existingProfile);
      
      if (existingProfile) {
        setFullName(existingProfile.fullName);
        setEmail(existingProfile.email);
        setPhone(existingProfile.phone);
        setNationalId(existingProfile.nationalId);
        setScfhsNumber(existingProfile.scfhsNumber || '');
        setEmploymentStatus(existingProfile.employmentStatus);
        setVolunteerAreas(existingProfile.volunteerAreas || '');
      } else {
        // Pre-fill with user data
        const firstName = userData.firstNameEn || userData.firstNameAr || '';
        const lastName = userData.lastNameEn || userData.lastNameAr || '';
        setFullName(`${firstName} ${lastName}`.trim());
        setEmail(userData.email || '');
        setPhone(userData.phone || '');
        setNationalId('');
        setScfhsNumber('');
        setEmploymentStatus('');
        setVolunteerAreas('');
      }
    } catch (error) {
      console.error('Error loading volunteer profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!fullName || !email || !phone || !nationalId || !employmentStatus) {
      toast.error(t('dashboard.volunteer.profile.errors.fillRequired') || 'Please fill all required fields');
      return;
    }

    // Validate phone number (10 digits starting with 05)
    if (!/^05\d{8}$/.test(phone)) {
      toast.error(t('dashboard.volunteer.profile.errors.invalidPhone') || 'Phone number must be 10 digits starting with 05');
      return;
    }

    // Validate national ID (10 digits)
    if (!/^\d{10}$/.test(nationalId)) {
      toast.error(t('dashboard.volunteer.profile.errors.invalidNationalId') || 'National ID must be 10 digits');
      return;
    }

    if (!userData?.id) return;

    setSaving(true);
    try {
      if (profile) {
        // Update existing profile
        await updateVolunteerProfile(profile.id, {
          fullName,
          email,
          phone,
          nationalId,
          scfhsNumber: scfhsNumber || undefined,
          employmentStatus,
          volunteerAreas: volunteerAreas || undefined,
        });
        toast.success(t('dashboard.volunteer.profile.success.updated') || 'Profile updated successfully');
      } else {
        // Create new profile
        await createVolunteerProfile({
          userId: userData.id,
          fullName,
          email,
          phone,
          nationalId,
          scfhsNumber: scfhsNumber || undefined,
          employmentStatus,
          volunteerAreas: volunteerAreas || undefined,
        });
        toast.success(t('dashboard.volunteer.profile.success.created') || 'Profile created successfully');
      }
      setDialogOpen(false);
      loadProfile(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving volunteer profile:', error);
      toast.error(t('dashboard.volunteer.profile.errors.saveFailed') || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const isProfileComplete = profile && profile.fullName && profile.email && profile.phone && profile.nationalId && profile.employmentStatus;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('dashboard.volunteer.profile.title') || 'Volunteer Profile'}
          </CardTitle>
          <User className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {isProfileComplete ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-500">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{t('dashboard.volunteer.profile.complete') || 'Profile Complete'}</span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('dashboard.volunteer.profile.form.fullName') || 'Full Name'}:</span>
                      <p className="font-medium">{profile.fullName}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('dashboard.volunteer.profile.form.email') || 'Email'}:</span>
                      <p className="font-medium">{profile.email}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('dashboard.volunteer.profile.form.phone') || 'Phone'}:</span>
                      <p className="font-medium">{profile.phone}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('dashboard.volunteer.profile.form.employmentStatus') || 'Employment Status'}:</span>
                      <p className="font-medium capitalize">{profile.employmentStatus}</p>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setDialogOpen(true)}
                  >
                    <Edit2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {t('dashboard.volunteer.profile.edit') || 'Edit Profile'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {t('dashboard.volunteer.profile.incomplete') || 
                      'Complete your profile to help us serve you better'}
                  </p>
                  <Button
                    className="w-full"
                    onClick={() => setDialogOpen(true)}
                  >
                    <Edit2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {t('dashboard.volunteer.profile.complete') || 'Complete Profile'}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Profile Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" dir={dir}>
          <DialogHeader>
            <DialogTitle>
              {t('dashboard.volunteer.profile.editTitle') || 'Edit Volunteer Profile'}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.volunteer.profile.editDescription') || 
                'Update your volunteer information and credentials'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Full Name */}
            <div className="grid gap-2">
              <Label htmlFor="fullName">
                {t('dashboard.volunteer.profile.form.fullName') || 'Full Name'} *
              </Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('dashboard.volunteer.profile.form.fullNamePlaceholder') || 'Enter full name'}
              />
            </div>

            {/* Email */}
            <div className="grid gap-2">
              <Label htmlFor="email">
                {t('dashboard.volunteer.profile.form.email') || 'Email'} *
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
              />
            </div>

            {/* Phone */}
            <div className="grid gap-2">
              <Label htmlFor="phone">
                {t('dashboard.volunteer.profile.form.phone') || 'Mobile Number'} *
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="05xxxxxxxx"
                maxLength={10}
              />
            </div>

            {/* National ID */}
            <div className="grid gap-2">
              <Label htmlFor="nationalId">
                {t('dashboard.volunteer.profile.form.nationalId') || 'National ID Number'} *
              </Label>
              <Input
                id="nationalId"
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value)}
                placeholder={t('dashboard.volunteer.profile.form.nationalIdPlaceholder') || 
                  'Enter 10-digit National ID'}
                maxLength={10}
              />
            </div>

            {/* SCFHS Number */}
            <div className="grid gap-2">
              <Label htmlFor="scfhsNumber">
                {t('dashboard.volunteer.profile.form.scfhsNumber') || 'SCFHS Specialty Number'}
              </Label>
              <Input
                id="scfhsNumber"
                value={scfhsNumber}
                onChange={(e) => setScfhsNumber(e.target.value)}
                placeholder={t('dashboard.volunteer.profile.form.scfhsNumberPlaceholder') || 
                  'Enter SCFHS number if applicable'}
              />
            </div>

            {/* Employment Status */}
            <div className="grid gap-2">
              <Label htmlFor="employmentStatus">
                {t('dashboard.volunteer.profile.form.employmentStatus') || 'Employment Status'} *
              </Label>
              <Select value={employmentStatus} onValueChange={setEmploymentStatus}>
                <SelectTrigger id="employmentStatus">
                  <SelectValue placeholder={
                    t('dashboard.volunteer.profile.form.selectEmploymentStatus') || 
                    'Select employment status'
                  } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employed">
                    {t('dashboard.volunteer.profile.form.employed') || 'Employed'}
                  </SelectItem>
                  <SelectItem value="unemployed">
                    {t('dashboard.volunteer.profile.form.unemployed') || 'Unemployed'}
                  </SelectItem>
                  <SelectItem value="student">
                    {t('dashboard.volunteer.profile.form.student') || 'Student'}
                  </SelectItem>
                  <SelectItem value="retired">
                    {t('dashboard.volunteer.profile.form.retired') || 'Retired'}
                  </SelectItem>
                  <SelectItem value="other">
                    {t('dashboard.volunteer.profile.form.other') || 'Other'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Volunteer Areas */}
            <div className="grid gap-2">
              <Label htmlFor="volunteerAreas">
                {t('dashboard.volunteer.profile.form.volunteerAreas') || 'Preferred Volunteer Areas'}
              </Label>
              <Textarea
                id="volunteerAreas"
                value={volunteerAreas}
                onChange={(e) => setVolunteerAreas(e.target.value)}
                placeholder={t('dashboard.volunteer.profile.form.volunteerAreasPlaceholder') || 
                  'Enter preferred areas of volunteering...'}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'} animate-spin`} />
                  {t('common.saving') || 'Saving...'}
                </>
              ) : (
                t('common.save') || 'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

